/**
 * Document management utilities
 * Handles operations that involve both local and remote document management
 */

import { SQLiteService } from '../services/SQLiteService';
import { serverUrl } from '../config/environment';

export interface DocumentDeletionResult {
  localDeleted: boolean;
  cloudDeleted: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Delete document from remote server directly
 * Used when sync engine is not available
 */
async function deleteDocumentFromRemoteDirectly(documentId: string): Promise<boolean> {
  try {
    const response = await fetch(`${serverUrl}/api/documents/${documentId}/delete`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      return true;
    } else if (response.status === 404) {
      // Document doesn't exist on server, which is fine for deletion
      return true;
    } else {
      console.error(`Failed to delete document from server: ${response.status} ${response.statusText}`);
      return false;
    }
  } catch (error) {
    console.error('Network error while deleting document from server:', error);
    return false;
  }
}

/**
 * Delete document completely (both local and remote)
 * Uses proper separation of concerns:
 * - SQLiteService for local deletion
 * - Sync engine (via hook) for remote deletion
 */
export async function deleteDocumentCompletely(
  documentId: string,
  deleteFromRemote: () => Promise<boolean>
): Promise<DocumentDeletionResult> {
  const errors: string[] = [];
  const warnings: string[] = [];
  let localDeleted = false;
  let cloudDeleted = false;

  // Delete from cloud first (so if it fails, we still have local copy)
  try {
    cloudDeleted = await deleteFromRemote();
    if (!cloudDeleted) {
      warnings.push(`Cloud deletion returned false - document may or may not exist on server`);
    }
  } catch (error) {
    errors.push(`Cloud deletion failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }

  // Delete locally
  try {
    localDeleted = await SQLiteService.deleteDocumentDatabase(documentId);
    if (!localDeleted) {
      warnings.push(`Local deletion returned false - document may not exist locally`);
    }
  } catch (error) {
    errors.push(`Local deletion failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }

  return {
    localDeleted,
    cloudDeleted,
    errors,
    warnings
  };
}

/**
 * Delete document completely (both local and remote) - standalone version
 * Used when sync engine is not available (e.g., in AllDocumentsView)
 * Uses direct fetch for remote deletion
 */
export async function deleteDocumentCompletelyStandalone(
  documentId: string
): Promise<DocumentDeletionResult> {
  return deleteDocumentCompletely(documentId, () => deleteDocumentFromRemoteDirectly(documentId));
}
